import React, {
  ComponentType,
  ReactNode,
  useCallback,
  useEffect,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  RouteComponentProps,
  withRouter
} from 'react-router-dom'
import {
  Container,
  Icon,
  Popup,
  Segment
} from 'semantic-ui-react'
import concat from 'lodash/concat'
import { format } from 'date-fns'
import { Dispatch } from 'redux'
// Local
import { formatDob } from '../common/formatUtils'
import {
  API_URL,
  FORMAT,
  LIST_PAGE_SIZE,
  LIST_MAX_PAGES,
  PAGES,
  ROLES,
  shouldRenderReskin2025
} from '../../constant'
import Filter from '../common/filter'
import SubHeader from '../common/SubHeader'
import Table from '../common/Table'
import { patientMrn } from '../common/patientUtils'
import { semiColonsToLineBreaks } from '../v2/utils/utils'
import { api } from '../../action/api'
import { updatePagesSeen } from '../../action/encounter'
import { searchGenerator } from '../../action/search'
import { updateUserAppointmentlistFilters } from '../../action/user'
import {
  AppointmentlistFilter,
  Encounter,
  User
} from '../authenticated/types'
import { Filters } from '../../../types'
import If from '../common/if'
import ReveleerControlButton from '../common/ReveleerControlButton'
import FilterIcon from '../icons/Filter'
import { RootState } from '../../store'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeTable = Table as unknown as ComponentType<any>

const defaultSort = 'start desc'

// Filter metadata type
type FilterField = {
  placeholder: string
  name: string
  width: number
  like?: boolean
  date?: boolean
  practitionerSelect?: boolean
}

// Query filters type - date fields can be Date objects or formatted strings depending on context
type QueryFilters = {
  mrn: string | null
  start: Date | string | null
  birthDate: Date | string | null
  firstName: string | null
  lastName: string | null
  practitionerId: string | null
  onOffShore: string | null
}

// Table row data type
type TableRowData = {
  id: string | undefined
  time: ReactNode
  name: string
  name_icon: ReactNode | null
  dob: string
  mrn: ReactNode
  appointmentType: string
  nameMobile: string
  practitioner: string
  meta: {
    className: string
  }
}

// TODO - sboles - This is actually showing the appointment list.
// - Move this into its own top level module and name the file something obvious

/** Utility function for readability */
const elseNull = <T,>(variable: T): T | null => variable || null

/** extract the offshore query setting */
const extractOffshore = (
  filterObj: AppointmentlistFilter | QueryFilters
): string | null => {
  if ('onOffShore' in filterObj && filterObj.onOffShore) {
    return filterObj.onOffShore
  }
  if ('defaultPatientOffshoreFilter' in filterObj && filterObj.defaultPatientOffshoreFilter) {
    return filterObj.defaultPatientOffshoreFilter as string
  }
  return null
}

/** Build query object from component state or global state */
const buildQuery = (
  filterObj: AppointmentlistFilter | QueryFilters
): QueryFilters => {
  const {
    mrn,
    start,
    birthDate,
    firstName,
    lastName,
    practitionerId
  } = filterObj

  // convert strings to Date objects here
  return {
    mrn: elseNull(mrn),
    start: start
      ? new Date(start as number)
      : null,
    birthDate: birthDate
      ? new Date(birthDate as string)
      : null,
    firstName: firstName
      ? `${String(firstName).replaceAll('%', '')}%`
      : null,
    lastName: lastName
      ? `${String(lastName).replaceAll('%', '')}%`
      : null,
    practitionerId: elseNull(practitionerId),
    onOffShore: extractOffshore(filterObj)
  }
}

/** Get the values we want to show in the filter component. */
const filterInitialValues = (queryFilters: QueryFilters): QueryFilters => {
  // may have a full blown date object here, want to show a short date in the filter component
  // Do the opposite of buildQuery, and get back to a string
  const datefmt = (s: Date | string | null): string | null => s
    ? format(new Date(s), FORMAT.DATE)
    : null
  const textfmt = (t: string | null): string | null => t
    ? t.replaceAll('%', '')
    : null
  return {
    ...queryFilters,
    firstName: textfmt(queryFilters.firstName),
    lastName: textfmt(queryFilters.lastName),
    start: datefmt(queryFilters.start),
    birthDate: datefmt(queryFilters.birthDate)
  }
}

/** Utility function */
const formatEncounterDate = (
  startDt: Date | null | undefined,
  status: string | null | undefined
): ReactNode => {
  if (!startDt) return null
  return status === 'CANCELLED'
    ? (
      <Popup
        content="Cancelled"
        mouseEnterDelay={ 1000 }
        trigger={
          <s>
            { format(startDt, FORMAT.DATE_TIME) }
          </s>
        }
      />
    )
    : (
      <span>
        { format(startDt, FORMAT.DATE_TIME) }
      </span>
    )
}

/** Map encounter data into the proper display format. */
const generateTableData = (
  encounters: Encounter[] | undefined,
  userType: keyof typeof ROLES
): TableRowData[] => {
  if (!encounters) return []
  return encounters.map((record: Encounter) => {
    const { patient, practitioner } = record
    const mrn = patientMrn(patient)
    const patientFirstName = (patient && patient.firstName) || ''
    const patientLastName = (patient && patient.lastName) || ''
    const patientBirthDate = patient && patient.birthDate
    const practitionerFirstName = (practitioner && practitioner.firstName) || ''
    const practitionerLastName = (practitioner && practitioner.lastName) || ''
    return {
      id: record.id,
      time: formatEncounterDate(record.start, record.status),
      name: `${patientFirstName} ${patientLastName}`,
      name_icon: (
        userType === ROLES.CDI &&
        record.review &&
        record.review.reviewCode === 'LOCKED'
      )
        ? <Icon name="lock" />
        : null,
      dob: formatDob(patientBirthDate, FORMAT.DATE),
      mrn: semiColonsToLineBreaks(mrn || ''),
      appointmentType: record.appointmentType || '',
      nameMobile:
        `${patientFirstName
        } ${patientLastName
        } ( ${formatDob(patientBirthDate, FORMAT.DATE)
        } )`,
      practitioner: `${practitionerFirstName} ${practitionerLastName}`,
      meta: {
        className: 'cursor-pointer'
      }
    }
  })
}

const getFilterMetadata = (): FilterField[][] => [
  [
    { placeholder: 'Patient MRN', name: 'mrn', width: 6, like: false },
    { placeholder: 'First Name', name: 'firstName', width: 5, like: true },
    { placeholder: 'Last Name', name: 'lastName', width: 5, like: true }
  ],
  [
    { placeholder: 'Date of Birth', name: 'birthDate', width: 6, date: true },
    { placeholder: 'Appointment Date', name: 'start', width: 5, date: true },
    { placeholder: 'Practitioner', name: 'practitionerId', width: 5, practitionerSelect: true }
  ]
]

const mapStateToProps = (state: RootState) => ({
  userType: state.user.type,
  appointmentlistFilters: state.user.appointmentlistFilters,
  pagesSeen: state.encounter.pagesSeen,
  currentUser: state.user
})

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  updateUserAppointmentlistFiltersProp: (
    user: User,
    filters: Filters | QueryFilters
  ) => (
    dispatch(updateUserAppointmentlistFilters(
      user,
      filters as Filters
    ))
  ),
  updatePagesSeenProp: (pagesSeen: number) => (
    dispatch(updatePagesSeen(pagesSeen))
  )
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>
type ReduxDispatchProps = ReturnType<typeof mapDispatchToProps>

type Props = ReduxStateProps & ReduxDispatchProps & RouteComponentProps

/** Display a list of appointments, but they are called encounters in our api. */
function Appointments(props: Props): JSX.Element {
  const {
    userType,
    appointmentlistFilters,
    pagesSeen,
    currentUser,
    updateUserAppointmentlistFiltersProp,
    updatePagesSeenProp,
    history
  } = props

  // Initialize state from Redux
  const initialQuery = buildQuery(
    appointmentlistFilters.length > 0
      ? appointmentlistFilters[0]
      : {}
  )
  const shouldShowFilterInitially = Object.values(initialQuery).some(e => Boolean(e))

  // Component state
  const [
    shouldShowFilter,
    setShouldShowFilter
  ] = useState<boolean>(shouldShowFilterInitially)
  const [
    pagesDisplayed,
    setPagesDisplayed
  ] = useState<number>(pagesSeen)
  const [
    queryFilters,
    setQueryFilters
  ] = useState<QueryFilters>(filterInitialValues(initialQuery))
  const [
    encounters,
    setEncounters
  ] = useState<Encounter[] | undefined>(undefined)

  /** Fetch encounters based on filter criteria, if any. This is the workhorse method. */
  const fetchEncounters = useCallback((
    offset: number,
    query: QueryFilters | null,
    pagesToDisplay: number
  ): void => {
    const searchQuery = query || {} as QueryFilters
    const url = API_URL.ENCOUNTER_SEARCH_WITH_ROLE(userType)
    const limit = offset === 0 ? (LIST_PAGE_SIZE * pagesToDisplay) + 1 : LIST_PAGE_SIZE
    const params = searchGenerator(searchQuery as Record<string, unknown>, limit, offset, defaultSort, undefined)

    api.get(url, { params }).then(({ data }) => {
      const results = offset === 0 ? data : concat(encounters, data)
      setEncounters(results as Encounter[])
      setPagesDisplayed(pagesToDisplay)
      setQueryFilters(searchQuery)
    })
  }, [ userType, encounters ])

  /** Load the default search parameters for encounter data. */
  useEffect(() => {
    const offset = 0
    const query = buildQuery(queryFilters)
    fetchEncounters(offset, query, pagesDisplayed)
    // Only run on mount - equivalent to componentDidMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Load a selected appointment */
  const selectRecord = useCallback((id: string): void => {
    history.push(`${PAGES.ENCOUNTER_DETAIL}${id}`)
  }, [ history ])

  /** Toggle whether to display the advanced search or not */
  const toggleFilter = useCallback((): void => {
    setShouldShowFilter(prev => !prev)
  }, [])

  /** Initial search for encounters for a given set of filters. */
  const searchWithFilter = useCallback((query: QueryFilters): void => {
    // query comes directly from the Filter component
    updateUserAppointmentlistFiltersProp(
      currentUser,
      query as unknown as Filters
    )
    const offset = 0
    fetchEncounters(offset, query, pagesDisplayed)
  }, [
    currentUser,
    fetchEncounters,
    pagesDisplayed,
    updateUserAppointmentlistFiltersProp
  ])

  /** Load additional encounters based on existing search criteria. */
  const onMore = useCallback((): void => {
    const query = buildQuery(queryFilters)
    const offset = LIST_PAGE_SIZE * pagesDisplayed
    const newPagesDisplayed = pagesDisplayed + 1

    fetchEncounters(offset, query, newPagesDisplayed)
    // update the Redux global state
    updatePagesSeenProp(newPagesDisplayed)
  }, [
    queryFilters,
    pagesDisplayed,
    fetchEncounters,
    updatePagesSeenProp
  ])

  // Generate table data
  const tableData = generateTableData(encounters, userType)

  let shouldDisplayMore = false
  let shouldDisplayMaxRecords = false
  if (tableData.length > LIST_PAGE_SIZE * pagesDisplayed) {
    shouldDisplayMore = pagesDisplayed < LIST_MAX_PAGES
    shouldDisplayMaxRecords = pagesDisplayed >= LIST_MAX_PAGES
    tableData.pop()
  }

  return (
    <div className="header-padding">
      <Container>
        <SubHeader
          left="Appointments"
          right={
            shouldRenderReskin2025
              ? (
                <ReveleerControlButton
                  icon={ FilterIcon }
                  id="worklist-filter"
                  isDisabled={ false }
                  onClick={ toggleFilter }
                />
              )
              : (
                <Icon
                  name="filter"
                  size="large"
                  className="pointer"
                  onClick={ toggleFilter }
                />
              )
          }
        />
        <If isTrue={ shouldShowFilter }>
          <Filter
            submit={ searchWithFilter }
            filters={ getFilterMetadata() }
            initialValues={ queryFilters }
            onChange={ undefined }
            updateUserPatientListFilters={ () => null }
            setInitialValuesState={ () => null }
            onClear={ undefined }
            buttonText=""
          />
        </If>
        <SafeSegment
          attached
          className="no-margin"
        >
          <SafeTable
            computerColumns={ [
              { name: 'Time', value: 'time' },
              { name: 'Type', value: 'appointmentType' },
              { name: 'Name', value: 'name' },
              { name: 'DOB', value: 'dob' },
              { name: 'MRN(s)', value: 'mrn' },
              { name: 'Practitioner', value: 'practitioner' }
            ] }
            mobileColumns={
              [
                {
                  value: 'time',
                  width: '4'
                },
                {
                  value: 'nameMobile',
                  width: '12'
                }
              ]
            }
            data={ tableData }
            onClick={ selectRecord }
            more={ shouldDisplayMore }
            max={ shouldDisplayMaxRecords }
            onMore={ onMore }
          />
        </SafeSegment>
      </Container>
    </div>
  )
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Appointments)
)
